/**
 * HSE raporları metinleri (ADR 0049): Non-Conformity (PSC dahil), Deficiency, Near Miss, Accident;
 * liste, kayıt, faaliyetler, kapanış ve hata kodları iki dilde (`locales.test.ts`) [NR496 4.1.5].
 */

/** Ekranların alabildiği hata kodları; `locales.test.ts` iki dilde metnini arar. */
export const HSE_ERROR_CODES = [
  'GENERIC',
  'NETWORK',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'ROW_VERSION_CONFLICT',
  'INVALID_HSE_REPORT',
  'INVALID_HSE_ACTION',
  'INVALID_HSE_FINDING',
  'INVALID_HSE_COMMENT',
  'INVALID_HSE_NOTE',
  'HSE_DRAFT_OWNER',
  'HSE_REPORT_SUBMITTED',
  'HSE_NOT_SUBMITTED',
  'HSE_NOT_REVIEWED',
  'HSE_ALREADY_REVIEWED',
  'HSE_PENDING_CLOSURE',
  'HSE_CLOSED',
  'HSE_NOT_CLOSABLE',
  'HSE_ACTION_FINISHED',
  'HSE_FINDING_TYPE',
  'HSE_FINDING_IN_USE',
  'HSE_FINDING_MISMATCH',
  'HSE_PARENT_INVALID',
  'HSE_REQUEST_USED',
  'HSE_NUMBER_FIXED',
  'EVIDENCE_REQUIRED',
  'COMPLETION_NOTE_REQUIRED',
  'INVALID_ACTION_TRANSITION',
  'CANCEL_REASON_REQUIRED',
  'DISCARD_REASON_REQUIRED',
  'FUTURE_DATE',
  'VESSEL_MISMATCH',
  'RISK_ASSESSMENT_NOT_AVAILABLE',
  'REAUTHENTICATION_REQUIRED',
  'INVALID_CREDENTIALS',
  'TOO_MANY_ATTEMPTS',
  'NO_ELIGIBLE_APPROVER',
  'SELF_APPROVAL_FORBIDDEN',
  'SAME_APPROVER_FORBIDDEN',
  'NOT_APPROVER',
  'OFFICE_STEP',
  'NOT_OFFICE_STEP',
  'NOT_PENDING',
  'STALE_DECISION',
  'CONTENT_CHANGED',
  'RETURN_REASON_REQUIRED',
  'REOPEN_REASON_REQUIRED',
  'NOT_FINAL',
  'ALREADY_SUBMITTED',
  'FILE_NOT_UPLOADED',
  'FILE_TOO_LARGE',
  'UNSUPPORTED_FILE_TYPE',
  'HSE_WORK_ORDER_EXISTS',
  'EQUIPMENT_REQUIRED',
] as const;

const CHECKS_EN = {
  NOT_SUBMITTED: 'Report submitted',
  NOT_REVIEWED: 'Reviewed by the master',
  NO_ACTIONS: 'At least one action assigned',
  ACTIONS_OPEN: 'All actions completed with evidence',
  PREVENTIVE_ACTION: 'Preventive action to stop recurrence (ISM 9.2)',
  ROOT_CAUSE: 'Root cause identified (at least 20 characters)',
  VERIFICATION_NOTE: 'Effectiveness verification recorded',
  FINDING_WITHOUT_ACTION: 'Every PSC remark / finding has an action',
  TEST_RESULT: 'Repair and function test result recorded',
  FIT_FOR_SERVICE: 'Equipment confirmed fit for service',
  LESSONS_LEARNED: 'Lessons learned recorded',
  RESIDUAL_RISK: 'Residual risk reduced to low or medium',
  INVESTIGATION: 'Investigation recorded',
  NOTIFICATION_DECISION: 'Notification decision made (required / not required)',
  NOTIFICATION_PENDING: 'Every required notification has a date',
  EFFECTIVENESS: 'Corrective action verified as effective',
};

const CHECKS_TR = {
  NOT_SUBMITTED: 'Rapor gönderildi',
  NOT_REVIEWED: 'Kaptan inceledi',
  NO_ACTIONS: 'En az bir faaliyet atandı',
  ACTIONS_OPEN: 'Bütün faaliyetler kanıtla tamamlandı',
  PREVENTIVE_ACTION: 'Tekrarını önleyici faaliyet var (ISM 9.2)',
  ROOT_CAUSE: 'Kök neden belirlendi (en az 20 karakter)',
  VERIFICATION_NOTE: 'Etkinlik doğrulaması yazıldı',
  FINDING_WITHOUT_ACTION: 'Her PSC remark’ının / bulgunun faaliyeti var',
  TEST_RESULT: 'Onarım ve fonksiyon testi sonucu yazıldı',
  FIT_FOR_SERVICE: 'Ekipmanın hizmete uygun olduğu teyit edildi',
  LESSONS_LEARNED: 'Alınan dersler yazıldı',
  RESIDUAL_RISK: 'Kalıntı risk düşük ya da orta seviyeye indi',
  INVESTIGATION: 'İnceleme yazıldı',
  NOTIFICATION_DECISION: 'Bildirim kararı verildi (gerekli / gerekli değil)',
  NOTIFICATION_PENDING: 'Gerekli her bildirimin tarihi girildi',
  EFFECTIVENESS: 'Düzeltici faaliyetin etkin olduğu doğrulandı',
};

export const hseEn = {
  types: {
    non_conformity: 'Non Conformity',
    deficiency: 'Deficiency',
    near_miss: 'Near Miss',
    accident: 'Accident',
  },
  newTitle: {
    non_conformity: 'New Non Conformity',
    deficiency: 'New Deficiency',
    near_miss: 'New Near Miss',
    accident: 'New Accident',
  },
  descriptions: {
    non_conformity:
      'Deviation from ISM, SMS, legal or company procedures (e.g. missing document, incorrect procedure).',
    deficiency:
      'Equipment, system or condition that is not working as it should (e.g. broken equipment, expired certificate).',
    near_miss:
      'An incident that did not result in injury, damage or loss, but had the potential to do so.',
    accident: 'An unplanned event resulting in injury, illness, damage or environmental harm.',
  },
  statuses: {
    draft: 'Draft',
    submitted: 'Submitted',
    under_review: 'Under Review',
    action_assigned: 'Action Assigned',
    in_progress: 'In Progress',
    pending_verification: 'Pending Verification',
    closed: 'Closed',
    reopened: 'Reopened',
  },
  priorities: { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' },
  departments: {
    deck: 'Deck',
    engine: 'Engine',
    electrical: 'Electrical',
    bridge: 'Bridge',
    galley: 'Galley',
    accommodation: 'Accommodation',
    safety: 'Safety',
    office: 'Office',
    other: 'Other',
  },
  categories: {
    non_conformity: {
      procedure: 'Procedure',
      instruction: 'Instruction Violation',
      training: 'Training',
      administrative: 'Administrative',
      equipment: 'Equipment',
      documentation: 'Documentation',
      psc: 'PSC (Port State Control)',
      other: 'Other',
    },
    deficiency: {
      equipment: 'Equipment',
      maintenance: 'Maintenance',
      safety_equipment: 'Safety Equipment',
      documentation: 'Documentation',
      other: 'Other',
    },
    near_miss: {
      collision_risk: 'Collision Risk',
      slip_trip_fall: 'Slip / Trip / Fall',
      equipment_failure: 'Equipment Failure',
      fire_risk: 'Fire Risk',
      other: 'Other',
    },
    accident: {
      personnel_injury: 'Personnel Injury',
      property_damage: 'Property Damage',
      environmental: 'Environmental Pollution',
      vessel_damage: 'Damage to Vessel',
      other: 'Other',
    },
  },
  subCategories: {
    machinery: 'Machinery',
    electrical: 'Electrical',
    navigation: 'Navigation',
    safety: 'Safety',
    hull_deck: 'Hull / Deck',
    cargo: 'Cargo',
    accommodation: 'Accommodation',
    other: 'Other',
  },
  sources: {
    internal_audit: 'Internal Audit',
    external_audit: 'External Audit',
    psc_inspection: 'PSC Inspection',
    flag_inspection: 'Flag State Inspection',
    class_survey: 'Class Survey',
    vetting: 'Vetting / Charterer Inspection',
    self_detected: 'Detected on Board',
    other: 'Other',
  },
  ncClasses: { major: 'Major Non-Conformity', minor: 'Non-Conformity', observation: 'Observation' },
  mous: {
    paris: 'Paris MOU',
    tokyo: 'Tokyo MOU',
    uscg: 'USCG',
    black_sea: 'Black Sea MOU',
    mediterranean: 'Mediterranean MOU',
    indian_ocean: 'Indian Ocean MOU',
    other: 'Other',
  },
  pscDocuments: { psc_report: 'PSC Report', form_a: 'Form A', form_b: 'Form B', other: 'Other' },
  pscFindings: {
    no_deficiencies: 'No Deficiencies',
    remarks_issued: 'Remarks Issued',
    deficiencies: 'Deficiencies Recorded',
    detention: 'Detention',
  },
  usability: { yes: 'Usable', restricted: 'Usable with Restrictions', no: 'Not Usable' },
  yesNo: { yes: 'Yes', no: 'No' },
  externalAuthorities: { psc: 'PSC', flag: 'Flag', class: 'Class', other: 'Other' },
  likelihoodScale: {
    '1': '1 – Rare',
    '2': '2 – Unlikely',
    '3': '3 – Possible',
    '4': '4 – Likely',
    '5': '5 – Almost certain',
  },
  severityScale: {
    '1': '1 – Negligible',
    '2': '2 – Minor',
    '3': '3 – Moderate',
    '4': '4 – Major',
    '5': '5 – Catastrophic',
  },
  actionKinds: { correction: 'Correction', corrective: 'Corrective', preventive: 'Preventive' },
  actionKindHints: {
    correction: 'Fixes the problem found (e.g. replace the missing extinguisher).',
    corrective: 'Removes the cause of the problem.',
    preventive: 'Prevents recurrence (ISM 9.2), e.g. a new check or procedure change.',
  },
  actionStatuses: {
    planned: 'Planned',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  findingStatuses: {
    open: 'Open',
    planned: 'Planned',
    in_progress: 'In Progress',
    closed: 'Closed',
  },
  notificationDecisions: {
    required: 'Required',
    not_required: 'Not Required',
    undetermined: 'Not yet decided',
  },
  recipients: {
    flag_state: 'Flag State',
    class: 'Classification Society',
    port_state: 'Port State',
    coastal_state: 'Coastal State',
    p_and_i: 'P&I Club',
    company: 'Company / DPA',
    other: 'Other',
  },
  noteKinds: {
    comment: 'Office comment',
    action_request: 'Additional action requested',
    reopen_request: 'Reopening requested',
    report_request: 'Report requested by office',
  },
  list: {
    dateRange: 'Date range',
    dateFrom: 'From',
    dateTo: 'To',
    requestReport: 'Request report',
    kpi: {
      total: 'Total',
      open: 'Open',
      pendingReview: 'Pending Review',
      overdue: 'Overdue',
      closed: 'Closed',
    },
    table: '{{type}} records',
    filters: 'Filters',
    search: 'Search',
    searchPlaceholder: 'Search {{type}}…',
    status: 'Status',
    allStatus: 'All statuses',
    priority: 'Priority',
    allPriority: 'All priorities',
    vessel: 'Vessel',
    allVessels: 'All vessels',
    emptyTitle: 'No {{type}} records yet',
    emptyShip: 'Anyone on board can open a report with the "New" button.',
    emptyWeb: 'Reports are opened on board and appear here after the next synchronisation.',
    noMatch: 'No records match the filters',
    columns: {
      reportNo: 'ID',
      type: 'Type',
      date: 'Date',
      vessel: 'Vessel',
      category: 'Category',
      description: 'Description',
      status: 'Status',
      priority: 'Priority',
      responsible: 'Responsible',
    },
    open: 'Open {{no}}',
    showing: 'Showing {{from}} - {{to}} of {{total}}',
    pagination: 'Pages',
    overdue: 'Overdue',
    officeRequests: {
      title: 'Office requests ({{count}})',
      open: 'Open report',
    },
    error: 'Reports could not be loaded',
    retry: 'Try again',
  },
  tabs: {
    general: 'Form',
    psc: 'PSC Details',
    related: 'Related Deficiencies',
    investigation: 'Investigation',
    actions: 'Corrective Actions',
    attachments: 'Attachments',
    comments: 'Comments',
    closure: 'Verification & Closure',
    history: 'Workflow / History',
  },
  record: {
    workflow: 'Workflow',
    workflowTitle: '{{type}} workflow',
    loading: 'Loading report',
    fixFields: 'Please complete the highlighted fields.',
    submitted: 'Report submitted.',
    saved: 'Saved.',
    breadcrumb: 'Breadcrumb',
    saveDraft: 'Save Draft',
    submit: 'Submit',
    save: 'Save changes',
    discard: 'Discard draft',
    discardTitle: 'Discard draft',
    discardBody:
      'The draft is not deleted; it is kept with your reason and its number is not reused.',
    review: 'Review',
    generalTitle: '1. General Information',
    side: {
      psc: 'PSC Inspection Details',
      sms: 'SMS Reference',
      related: 'Related Information',
      risk: 'Risk & Immediate Action',
      accident: 'Injury, Damage & Environment',
    },
    pscHint:
      'Enter each PSC remark in "PSC Remark Details" after saving; codes are entered as written in the PSC report.',
    investigationTitle: 'Investigation & Root Cause',
    notificationsTitle: 'Notifications',
    notificationsHint:
      'Notification requirements and deadlines depend on the flag state and the company SMS; the system only records the decision and when each party was notified.',
    rail: 'Related links and documents',
  },
  fields: {
    initialAssessment: 'Master’s Initial Assessment',
    consequences: 'Potential Consequences',
    existingControls: 'Existing Controls',
    likelihood: 'Likelihood',
    severity: 'Severity',
    workDone: 'Work Done',
    impedesOperation: 'Impedes operation',
    externalAuthority: 'External reference (PSC / Flag / Class)',
    externalReference: 'Reference No',
    closeBy: 'Closing deadline',
    conditions: 'Near-miss conditions',
    humanFactors: 'Human factors',
    potentialHarm: 'Potential harm',
    firstResponse: 'First response',
    pollution: 'Environmental pollution',
    equipmentDamage: 'Equipment damage',
    injury: 'Injury',
    timeline: 'Event timeline',
    verificationDate: 'Verification Date',
    verifiedBy: 'Verified By',
    vessel: 'Vessel',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    priority: 'Priority',
    location: 'Location',
    department: 'Department',
    reportedBy: 'Reported By',
    category_non_conformity: 'Non Conformity Type',
    category_deficiency: 'Category',
    category_near_miss: 'Category',
    category_accident: 'Category',
    source: 'Source',
    subCategory: 'Sub Category',
    title: 'Title / Short Description',
    description: 'Description',
    pscAuthority: 'PSC Authority',
    mou: 'MOU',
    pscPort: 'Port',
    inspectionDate: 'Inspection Date',
    inspectionNo: 'Inspection No',
    inspectorName: 'Inspector Name',
    reportNo: 'Report No',
    issuedDocument: 'Issued Document',
    remarkReference: 'Remark Reference',
    overallFinding: 'Overall Finding',
    findingNote: 'Finding note',
    smsReference: 'Violated ISM / SMS Clause',
    ncClass: 'Classification',
    rootCauseHypothesis: 'Initial root cause hypothesis',
    proposedAction: 'Proposed corrective action',
    relatedAsset: 'Related Asset',
    assetNo: 'Asset No',
    locationDetail: 'Location (Detailed)',
    equipmentUsable: 'Equipment usable',
    operationalImpact: 'Operational impact',
    temporaryMeasure: 'Temporary measure / spare equipment',
    pmsWorkOrder: 'PMS work order',
    pmsDefect: 'PMS defect',
    immediateAction: 'Immediate Action',
    worstOutcome: 'Worst credible outcome',
    riskLevel: 'Risk level',
    riskMethod: 'Risk assessment method',
    affected: 'Persons / systems that could have been affected',
    preventingFactors: 'Factors that prevented or limited the event',
    injuredCount: 'Injured persons',
    emergencyResponse: 'Emergency response',
    injuries: 'Injuries / health effects (no medical records)',
    damage: 'Damage',
    environmental: 'Environmental consequences',
    investigation: 'Investigation',
    rootCause: 'Root Cause',
    manufacturer: 'Manufacturer',
    model: 'Model',
    spareEquipment: 'Spare equipment',
    serviceNeed: 'Service / spare parts needed',
    chronology: 'Chronology',
    similarEvents: 'Similar past events',
    shareWithFleet: 'Share lessons with other vessels',
    witnesses: 'Witness statements',
    medicalMeasures: 'Medical measures taken',
    contributingFactors: 'Contributing factors',
    losses: 'Losses',
    lessonsLearned: 'Lessons Learned',
    responsible: 'Responsible',
    targetDate: 'Target Date',
    parentReport: 'Main event (linked report)',
    formRecord: 'Related ISM form record',
    notificationDecision: 'Notification required',
    notificationReason: 'Reason / authority reference',
    verificationNote: 'Effectiveness verification',
    testResult: 'Repair / function test result',
    fitForService: 'Fit for service',
    residualRisk: 'Residual risk',
  },
  hseq: {
    title: 'HSEQ Reports',
    description:
      'Non-Conformity, Deficiency, Near Miss and Accident reports with one workflow: Reported, Under Action, Verification, Closed.',
    newReport: 'New Report',
    chooseType: 'Choose the report type',
    allTypes: 'All',
    typeFilter: 'Report type',
    typeNames: {
      non_conformity: 'Non-Conformity',
      deficiency: 'Deficiency',
      near_miss: 'Near Miss',
      accident: 'Accident',
    },
    stage: 'Stage',
    allStages: 'All stages',
    stages: {
      reported: 'Reported',
      under_action: 'Under Action',
      verification: 'Verification',
      closed: 'Closed',
    },
    stageNotes: {
      reported: 'Report submitted',
      under_action: 'Actions in progress',
      verification: 'Effectiveness check',
      closed: 'Report closed',
    },
    stepper: 'Report stages',
    form: 'Form',
    sections: {
      details: 'Report Details',
      initial: 'Initial Assessment',
      risk: 'Risk Assessment',
      investigation: 'Investigation / Root Cause',
      plan: 'Corrective Action Plan',
      linked: 'Linked Records',
      implementation: 'Implementation',
      verification: 'Verification',
      review: 'Management / DPA Review',
      closure: 'Closure',
    },
    sectionHints: {
      details: 'What happened, where and when',
      initial: 'Master’s first evaluation and immediate measures',
      risk: 'Potential consequences, existing controls, likelihood and severity',
      investigation: 'Investigation and root cause',
      plan: 'Activities, responsible persons and target dates',
      linked: 'Defect, work order and purchase records of this report',
      implementation: 'Work done, materials used and evidence',
      verification: 'Completion and effectiveness check',
      review: 'Office evaluation and decision',
      closure: 'Closing the report',
    },
    readOnly: 'Read only',
    typeDetails: {
      non_conformity: 'Non-Conformity details',
      deficiency: 'Deficiency details',
      near_miss: 'Near Miss details',
      accident: 'Accident details',
    },
    meta: {
      created: 'Created: {{at}}',
      createdBy: 'Created by: {{name}}',
      type: 'Type: {{type}}',
      category: 'Category: {{category}}',
      priority: 'Priority',
    },
    risk: {
      level: 'Risk Level',
      notRated: 'Not rated',
      score: 'Score {{score}}',
      matrixHint: 'Likelihood × severity in the company risk matrix.',
      levels: {
        very_low: 'Very Low',
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        very_high: 'Very High',
        critical: 'Critical',
      },
    },
    recurrence: {
      title: 'Recurrence check',
      none: 'No similar non-conformity on this vessel.',
      found_one: '{{count}} similar non-conformity on this vessel (same clause):',
      found_other: '{{count}} similar non-conformities on this vessel (same clause):',
    },
    implementation: {
      materials: 'Materials Used',
      item: 'Part / Code',
      code: 'Code',
      quantity: 'Quantity',
      unit: 'Unit',
      fromStock: 'From stock',
      requisition: 'Purchase',
      add: 'Add material',
      remove: 'Remove',
      empty: 'No materials recorded.',
      evidence: 'Evidence',
      noEvidence: 'No evidence yet.',
      yes: 'Yes',
      no: 'No',
    },
    verification: {
      effective: 'Was the corrective action effective?',
      yes: 'Yes',
      no: 'No',
      date: 'Verification Date',
      verifiedBy: 'Verified By',
      comments: 'Comments',
    },
    review: {
      empty: 'No office evaluation yet.',
      officeNotes: 'Office notes',
      steps: 'Closure approval',
      noSteps: 'The approval steps appear when the report is submitted for closure.',
    },
    linked: {
      defect: 'Defect',
      workOrder: 'Work Order',
      requisition: 'Purchase Requisition',
      equipment: 'Equipment',
      none: 'Not linked',
      linked: 'Linked',
      cancelled: 'Cancelled',
      parent: 'Main event',
      children: 'Related reports',
      open: 'Open {{no}}',
    },
    quick: {
      title: 'Quick Actions',
      createDefect: 'Create Defect',
      createWorkOrder: 'Create Work Order',
      createRequisition: 'Create Purchase Requisition',
      smsUpdate: 'SMS Procedure Update',
      copy: 'Copy Report',
      close: 'Close Report',
      closeBlocked: 'Closing needs: {{gaps}}',
      afterSubmit: 'Quick actions are available after the report is submitted.',
    },
    workOrder: {
      title: 'Create Work Order',
      exists: 'Work order {{no}} is already linked to this report; a second one is not opened.',
      open: 'Open work order',
      equipment: 'Equipment',
      name: 'Work order title',
      target: 'Target date',
      create: 'Create work order',
      created: 'Work order {{no}} created and linked to the report.',
      reused: 'The repair work order {{no}} of the linked defect is now linked to this report.',
      dateDropped:
        'Work order {{no}} created and linked to the report. The chosen target date could not be saved on this vessel server; correct it in the PMS work order.',
      fromDefect:
        'If the linked defect already has a repair work order, that work order is linked instead of opening a new one.',
    },
    sms: {
      title: 'SMS Procedure Update',
      needed: 'The SMS procedure needs updating',
      procedure: 'Procedure name / number',
      proposal: 'How can the procedure be improved?',
      save: 'Record',
      saved: 'The procedure update need is recorded in the report.',
      hint: 'The need is recorded in the report and printed on the PDF; the office follows it up with a document revision.',
      recorded: 'Procedure update needed: {{procedure}}',
    },
    copy: {
      loaded: 'Fields copied from {{no}}. Check them and save the new draft.',
    },
    timeline: {
      title: 'Status',
      created: 'Report created',
      submitted: 'Report submitted',
      reviewed: 'Under action',
      closure: 'Submitted for verification',
      closed: 'Closed',
      waiting: 'Waiting',
    },
    saveFirst: 'Save the report first.',
  },
  fieldErrors: {
    REQUIRED: 'Required',
    INVALID: 'Not a valid choice',
    TOO_LONG: 'Too long',
    INVALID_DATE: 'Not a valid date',
    INVALID_TIME: 'Not a valid date and time',
    INVALID_ID: 'Not a valid selection',
  },
  review: {
    title: 'Review report',
    confirm: 'Mark as reviewed',
    body: 'Record your initial assessment. After review, corrective actions can be assigned.',
    note: 'Review note',
  },
  actions: {
    title: 'Corrective Actions',
    afterSubmit: 'Actions are assigned after the report is submitted and reviewed.',
    afterReview: 'Actions can be assigned after the master’s review.',
    new: 'New Action',
    plan: 'Action Plan',
    progress: 'Progress',
    closure: 'Closure',
    empty: 'No actions yet.',
    no: 'No',
    description: 'Action Description',
    responsible: 'Responsible',
    dueDate: 'Due Date',
    status: 'Status',
    started: 'Started',
    completed: 'Completed',
    cancelled: 'Cancelled',
    noEvidence: 'No evidence yet: add a photo or document before completing.',
    start: 'Start',
    addEvidence: 'Add evidence',
    complete: 'Complete',
    cancel: 'Cancel action',
    cancelTitle: 'Cancel action',
    cancelBody: 'The action is kept in the history with your reason.',
    editTitle: 'Edit action',
    newTitle: 'New action',
    save: 'Save',
    assign: 'Assign',
    kind: 'Action type',
    person: 'Assigned person',
    finding: 'PSC remark / finding',
    workOrder: 'PMS work order',
    formRecord: 'ISM form record',
    completeTitle: 'Complete action #{{no}}',
    evidenceCount_one: '{{count}} evidence file attached.',
    evidenceCount_other: '{{count}} evidence files attached.',
    evidenceNeeded:
      'An action cannot be completed without evidence: attach a photo or document first, or link a reported PMS work order or a submitted ISM form record.',
    completionNote: 'What was done',
  },
  findings: {
    title: 'PSC Remark Details',
    subtitle: 'Remark Items',
    add: 'Add Remark',
    empty: 'No remarks yet.',
    afterSave: 'Save the report first, then add the remarks.',
    no: 'No',
    issue: 'Issue / Remark',
    category: 'Category',
    regulation: 'Regulation Ref.',
    dueDate: 'Due Date',
    status: 'Status',
    action: 'Action',
    edit: 'Edit',
    view: 'View',
    editTitle: 'Edit remark',
    newTitle: 'Add remark',
    save: 'Save',
    code: 'PSC code',
    codeHint: 'As written in the PSC report',
    actionTaken: 'Action taken code',
  },
  closure: {
    title: 'Closure',
    verificationTitle: 'Verification',
    checklist: 'Closure conditions',
    checks: CHECKS_EN,
    gaps: CHECKS_EN,
    steps: 'Approval steps',
    office: 'office',
    decisions: { approved: 'Approved', returned: 'Returned' },
    waiting: 'waiting',
    closedBanner: 'Closed. The record is locked; it can be reopened with a reason.',
    reopenedBanner:
      'Reopened: verification failed or the problem recurred. Add new actions; the previous record is kept.',
    submit: 'Submit for closure',
    submitTitle: 'Submit for closure',
    submitBody:
      'The report, its actions and evidence are locked while the closure is reviewed. The person who opened the report cannot close it alone.',
    saveFirst: 'Save your changes before submitting for closure.',
    approve: 'Approve closure',
    approveTitle: 'Approve closure',
    approveBody: 'Confirm that the actions are effective and the report can be closed.',
    return: 'Return',
    returnTitle: 'Return (verification failed)',
    returnBody: 'The report is reopened for further action. A reason is required.',
    reopen: 'Reopen',
    reopenTitle: 'Reopen report',
    reopenBody:
      'The closed record is not deleted; the reason and new actions are kept in the history.',
  },
  notes: {
    add: 'Add office note',
    title: 'Office note',
    send: 'Send to vessel',
    kind: 'Note type',
    body: 'Note',
  },
  requests: {
    title: 'Request a report from the vessel',
    send: 'Send request',
    body: 'Reports are written on board. The vessel opens the report from your request (e.g. an internal audit finding).',
    type: 'Report type',
  },
  files: {
    title: 'Photos / Documents',
    drop: 'Attach photos or documents',
    afterSave: 'Save the report first to attach files.',
    formats: 'Supported formats: JPG, PNG, PDF (max 25 MB)',
    choose: 'Choose file',
    empty: 'No files.',
    uploaded: 'File attached.',
    voided: 'Voided',
    states: {
      uploading: 'Uploading from vessel',
      on_vessel: 'File on vessel',
      rejected: 'Rejected',
    },
    reportFiles: 'Report files',
    actionFiles: 'Evidence of action',
  },
  links: {
    title: 'Related Links',
    empty: 'Links appear after the report is saved.',
    parent: 'Main event',
    children_one: 'Related records ({{count}})',
    children_other: 'Related records ({{count}})',
    workOrder: 'PMS work order',
    defect: 'PMS defect',
    formRecord: 'ISM form record',
    officeRequest: 'Office request',
    history: 'History',
  },
  comments: {
    title: 'Comments / Follow-up',
    afterSubmit: 'Comments are available after the report is saved.',
    empty: 'No comments yet.',
    ship: 'Vessel comment',
    add: 'Add a comment',
    send: 'Post comment',
  },
  history: {
    title: 'History / Audit Trail',
    empty: 'No history yet.',
    events: {
      created: 'Report opened',
      submitted: 'Report submitted',
      reviewed: 'Reviewed',
      action_assigned: 'Action assigned',
      action_in_progress: 'Action started',
      action_completed: 'Action completed',
      action_cancelled: 'Action cancelled',
      finding_added: 'PSC remark added',
      closure_submitted: 'Submitted for closure',
      closure_approved: 'Closure approved',
      closure_returned: 'Closure returned',
      reopened: 'Reopened',
      comment: 'Comment',
      office_comment: 'Office comment',
      office_action_request: 'Office requested additional action',
      office_reopen_request: 'Office requested reopening',
    },
  },
  related: {
    title: 'Related Deficiencies',
    subtitle: 'Records opened from the same event are linked to this report.',
    empty: 'No related records.',
    newDeficiency: 'New Deficiency',
  },
  notifications: {
    recipient: 'Notified party',
    name: 'Name',
    notifiedOn: 'Sent on',
    dueDate: 'Due by',
    method: 'Method',
    reference: 'Reference',
    remove: 'Remove',
    add: 'Add notification',
    empty: 'No notifications recorded.',
  },
  dialog: {
    close: 'Close',
    cancel: 'Cancel',
    reason: 'Reason',
    password: 'Password',
    passwordHint: 'The company asks for the password on every approval.',
  },
  errors: {
    GENERIC: 'The action could not be completed ({{code}}).',
    // Kaptana ait alan (ADR 0127)
    MASTER_ONLY:
      'Only the Master writes the Master’s initial assessment (ISM Code 5). When no Master is assigned, the deputy set in Settings may write it on behalf of the Master.',
    NETWORK: 'No connection to the server.',
    UNAUTHENTICATED: 'Your session has ended. Please sign in again.',
    FORBIDDEN: 'You do not have permission for this action.',
    NOT_FOUND: 'The report was not found.',
    VALIDATION_FAILED: 'Some entries are not valid.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    INVALID_HSE_REPORT: 'Please complete the required fields.',
    INVALID_HSE_ACTION: 'The action is not complete.',
    INVALID_HSE_FINDING: 'The remark is not complete.',
    INVALID_HSE_COMMENT: 'The comment is empty or too long.',
    INVALID_HSE_NOTE: 'The note is not complete.',
    HSE_DRAFT_OWNER: 'Only the person who opened the draft can change it.',
    HSE_REPORT_SUBMITTED: 'The report has already been submitted.',
    HSE_NOT_SUBMITTED: 'The report has not been submitted yet.',
    HSE_NOT_REVIEWED: 'Actions can be assigned after the master’s review.',
    HSE_ALREADY_REVIEWED: 'The report has already been reviewed.',
    HSE_PENDING_CLOSURE: 'The closure is waiting for approval; the report is locked.',
    HSE_CLOSED: 'The report is closed and locked.',
    HSE_NOT_CLOSABLE: 'The report cannot be submitted for closure yet:',
    HSE_ACTION_FINISHED: 'The action is completed or cancelled and cannot be changed.',
    HSE_FINDING_TYPE: 'Remarks can only be added to non-conformities.',
    HSE_FINDING_IN_USE: 'The remark has actions and cannot be removed.',
    HSE_FINDING_MISMATCH: 'The remark belongs to another report.',
    HSE_PARENT_INVALID: 'The main event cannot be this report or a linked record.',
    HSE_REQUEST_USED: 'A report has already been opened from this request.',
    HSE_NUMBER_FIXED: 'The report type and number cannot be changed.',
    EVIDENCE_REQUIRED:
      'An action cannot be completed without evidence. Attach a photo or document, or link a reported PMS work order or a submitted form record.',
    COMPLETION_NOTE_REQUIRED: 'Describe what was done.',
    INVALID_ACTION_TRANSITION: 'The action cannot move to this status.',
    CANCEL_REASON_REQUIRED: 'A reason is required.',
    DISCARD_REASON_REQUIRED: 'A reason is required.',
    FUTURE_DATE: 'The date cannot be in the future.',
    VESSEL_MISMATCH: 'The linked record belongs to another vessel.',
    RISK_ASSESSMENT_NOT_AVAILABLE: 'Risk assessments are not available yet.',
    REAUTHENTICATION_REQUIRED: 'Your password is required.',
    INVALID_CREDENTIALS: 'The password is not correct.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Please wait and try again.',
    NO_ELIGIBLE_APPROVER:
      'No one on board can approve this closure (the person who opened the report cannot approve it). Ask the office to check the approval matrix.',
    SELF_APPROVAL_FORBIDDEN: 'The person who opened or submitted the report cannot approve it.',
    SAME_APPROVER_FORBIDDEN: 'You have already approved another step of this closure.',
    NOT_APPROVER: 'Your role does not approve this step.',
    OFFICE_STEP: 'This step is decided in the office.',
    NOT_OFFICE_STEP: 'This step is decided on board.',
    NOT_PENDING: 'Nothing is waiting for a decision.',
    STALE_DECISION: 'The report changed while you were viewing it. Reload and try again.',
    CONTENT_CHANGED: 'The report changed after it was submitted. Reload and try again.',
    RETURN_REASON_REQUIRED: 'A reason is required to return.',
    REOPEN_REASON_REQUIRED: 'A reason is required to reopen.',
    NOT_FINAL: 'Only a closed report can be reopened.',
    ALREADY_SUBMITTED: 'The closure has already been submitted.',
    FILE_NOT_UPLOADED:
      'Evidence files have not reached the office yet. The decision is possible after they are uploaded and verified.',
    FILE_TOO_LARGE: 'The file is larger than 25 MB.',
    UNSUPPORTED_FILE_TYPE: 'Only JPG, PNG and PDF files can be attached.',
    HSE_WORK_ORDER_EXISTS: 'A work order is already linked to this report.',
    EQUIPMENT_REQUIRED: 'Choose the equipment of the work order.',
  },
};

export const hseTr: typeof hseEn = {
  types: {
    non_conformity: 'Uygunsuzluk',
    deficiency: 'Eksiklik',
    near_miss: 'Ramak Kala',
    accident: 'Kaza',
  },
  newTitle: {
    non_conformity: 'Yeni Uygunsuzluk',
    deficiency: 'Yeni Eksiklik',
    near_miss: 'Yeni Ramak Kala',
    accident: 'Yeni Kaza',
  },
  descriptions: {
    non_conformity:
      'ISM, SMS, yasal ya da şirket prosedürlerinden sapma (ör. eksik belge, yanlış prosedür).',
    deficiency:
      'Olması gerektiği gibi çalışmayan ekipman, sistem ya da durum (ör. arızalı ekipman, süresi dolmuş sertifika).',
    near_miss: 'Yaralanma, hasar ya da kayıpla sonuçlanmayan ama sonuçlanabilecek olay.',
    accident: 'Yaralanma, hastalık, hasar ya da çevresel zarara yol açan planlanmamış olay.',
  },
  statuses: {
    draft: 'Taslak',
    submitted: 'Gönderildi',
    under_review: 'İncelemede',
    action_assigned: 'Faaliyet Atandı',
    in_progress: 'Devam Ediyor',
    pending_verification: 'Doğrulama Bekliyor',
    closed: 'Kapandı',
    reopened: 'Yeniden Açıldı',
  },
  priorities: { low: 'Düşük', medium: 'Orta', high: 'Yüksek', critical: 'Kritik' },
  departments: {
    deck: 'Güverte',
    engine: 'Makine',
    electrical: 'Elektrik',
    bridge: 'Köprüüstü',
    galley: 'Kuzine',
    accommodation: 'Yaşam mahalli',
    safety: 'Emniyet',
    office: 'Ofis',
    other: 'Diğer',
  },
  categories: {
    non_conformity: {
      procedure: 'Prosedür',
      instruction: 'Talimat İhlali',
      training: 'Eğitim',
      administrative: 'İdari',
      equipment: 'Ekipman',
      documentation: 'Dokümantasyon',
      psc: 'PSC (Liman Devleti Kontrolü)',
      other: 'Diğer',
    },
    deficiency: {
      equipment: 'Ekipman',
      maintenance: 'Bakım',
      safety_equipment: 'Güvenlik Donanımı',
      documentation: 'Dokümantasyon',
      other: 'Diğer',
    },
    near_miss: {
      collision_risk: 'Çarpışma Tehlikesi',
      slip_trip_fall: 'Düşme / Kayma',
      equipment_failure: 'Ekipman Arızası',
      fire_risk: 'Yangın Tehlikesi',
      other: 'Diğer',
    },
    accident: {
      personnel_injury: 'Personel Yaralanması',
      property_damage: 'Maddi Hasar',
      environmental: 'Çevre Kirliliği',
      vessel_damage: 'Gemide Hasar',
      other: 'Diğer',
    },
  },
  subCategories: {
    machinery: 'Makine',
    electrical: 'Elektrik',
    navigation: 'Seyir',
    safety: 'Emniyet',
    hull_deck: 'Tekne / Güverte',
    cargo: 'Yük',
    accommodation: 'Yaşam mahalli',
    other: 'Diğer',
  },
  sources: {
    internal_audit: 'İç Denetim',
    external_audit: 'Dış Denetim',
    psc_inspection: 'PSC Denetimi',
    flag_inspection: 'Bayrak Devleti Denetimi',
    class_survey: 'Klas Sörveyi',
    vetting: 'Vetting / Kiracı Denetimi',
    self_detected: 'Gemide Tespit',
    other: 'Diğer',
  },
  ncClasses: { major: 'Büyük Uygunsuzluk', minor: 'Uygunsuzluk', observation: 'Gözlem' },
  mous: {
    paris: 'Paris MOU',
    tokyo: 'Tokyo MOU',
    uscg: 'USCG',
    black_sea: 'Karadeniz MOU',
    mediterranean: 'Akdeniz MOU',
    indian_ocean: 'Hint Okyanusu MOU',
    other: 'Diğer',
  },
  pscDocuments: { psc_report: 'PSC Raporu', form_a: 'Form A', form_b: 'Form B', other: 'Diğer' },
  pscFindings: {
    no_deficiencies: 'Eksiklik Yok',
    remarks_issued: 'Remark Verildi',
    deficiencies: 'Eksiklik Yazıldı',
    detention: 'Tutulma',
  },
  usability: { yes: 'Kullanılabilir', restricted: 'Kısıtlı Kullanılabilir', no: 'Kullanılamaz' },
  yesNo: { yes: 'Evet', no: 'Hayır' },
  externalAuthorities: { psc: 'PSC', flag: 'Bayrak', class: 'Klas', other: 'Diğer' },
  likelihoodScale: {
    '1': '1 – Çok düşük',
    '2': '2 – Düşük',
    '3': '3 – Olası',
    '4': '4 – Muhtemel',
    '5': '5 – Neredeyse kesin',
  },
  severityScale: {
    '1': '1 – Önemsiz',
    '2': '2 – Küçük',
    '3': '3 – Orta',
    '4': '4 – Büyük',
    '5': '5 – Felaket',
  },
  actionKinds: { correction: 'Düzeltme', corrective: 'Düzeltici', preventive: 'Önleyici' },
  actionKindHints: {
    correction: 'Bulunan sorunu giderir (ör. eksik söndürücüyü tamamla).',
    corrective: 'Sorunun nedenini ortadan kaldırır.',
    preventive: 'Tekrarını önler (ISM 9.2), ör. yeni kontrol ya da prosedür değişikliği.',
  },
  actionStatuses: {
    planned: 'Planlandı',
    in_progress: 'Devam Ediyor',
    completed: 'Tamamlandı',
    cancelled: 'İptal Edildi',
  },
  findingStatuses: {
    open: 'Açık',
    planned: 'Planlandı',
    in_progress: 'Devam Ediyor',
    closed: 'Kapandı',
  },
  notificationDecisions: {
    required: 'Gerekli',
    not_required: 'Gerekli Değil',
    undetermined: 'Henüz karar verilmedi',
  },
  recipients: {
    flag_state: 'Bayrak Devleti',
    class: 'Klas Kuruluşu',
    port_state: 'Liman Devleti',
    coastal_state: 'Kıyı Devleti',
    p_and_i: 'P&I Kulübü',
    company: 'Şirket / DPA',
    other: 'Diğer',
  },
  noteKinds: {
    comment: 'Ofis yorumu',
    action_request: 'Ek faaliyet istendi',
    reopen_request: 'Yeniden açma istendi',
    report_request: 'Ofisin rapor talebi',
  },
  list: {
    dateRange: 'Tarih aralığı',
    dateFrom: 'Başlangıç',
    dateTo: 'Bitiş',
    requestReport: 'Rapor iste',
    kpi: {
      total: 'Toplam',
      open: 'Açık',
      pendingReview: 'İnceleme Bekleyen',
      overdue: 'Geciken',
      closed: 'Kapanan',
    },
    table: '{{type}} kayıtları',
    filters: 'Süzgeçler',
    search: 'Ara',
    searchPlaceholder: '{{type}} ara…',
    status: 'Durum',
    allStatus: 'Bütün durumlar',
    priority: 'Öncelik',
    allPriority: 'Bütün öncelikler',
    vessel: 'Gemi',
    allVessels: 'Bütün gemiler',
    emptyTitle: 'Henüz {{type}} kaydı yok',
    emptyShip: 'Gemide herkes "Yeni" düğmesiyle rapor açabilir.',
    emptyWeb: 'Raporlar gemide açılır ve sonraki senkronda burada görünür.',
    noMatch: 'Süzgeçlere uyan kayıt yok',
    columns: {
      reportNo: 'No',
      type: 'Tür',
      date: 'Tarih',
      vessel: 'Gemi',
      category: 'Kategori',
      description: 'Açıklama',
      status: 'Durum',
      priority: 'Öncelik',
      responsible: 'Sorumlu',
    },
    open: '{{no}} aç',
    showing: '{{total}} kayıttan {{from}} - {{to}}',
    pagination: 'Sayfalar',
    overdue: 'Gecikmiş',
    officeRequests: {
      title: 'Ofis talepleri ({{count}})',
      open: 'Rapor aç',
    },
    error: 'Raporlar yüklenemedi',
    retry: 'Yeniden dene',
  },
  tabs: {
    general: 'Form',
    psc: 'PSC Ayrıntıları',
    related: 'İlgili Eksiklikler',
    investigation: 'İnceleme',
    actions: 'Düzeltici Faaliyetler',
    attachments: 'Ekler',
    comments: 'Yorumlar',
    closure: 'Doğrulama ve Kapanış',
    history: 'Workflow / Geçmiş',
  },
  record: {
    workflow: 'İş akışı',
    workflowTitle: '{{type}} iş akışı',
    loading: 'Rapor yükleniyor',
    fixFields: 'Lütfen işaretli alanları tamamlayın.',
    submitted: 'Rapor gönderildi.',
    saved: 'Kaydedildi.',
    breadcrumb: 'Konum',
    saveDraft: 'Taslağı Kaydet',
    submit: 'Gönder',
    save: 'Değişiklikleri kaydet',
    discard: 'Taslaktan vazgeç',
    discardTitle: 'Taslaktan vazgeç',
    discardBody: 'Taslak silinmez; gerekçenizle saklanır ve numarası yeniden kullanılmaz.',
    review: 'İncele',
    generalTitle: '1. Genel Bilgiler',
    side: {
      psc: 'PSC Denetim Bilgileri',
      sms: 'SMS Referansı',
      related: 'İlgili Bilgiler',
      risk: 'Risk ve İlk Müdahale',
      accident: 'Yaralanma, Hasar ve Çevre',
    },
    pscHint:
      'Kaydettikten sonra her PSC remark’ını "PSC Remark Ayrıntıları"na girin; kodlar PSC raporunda yazıldığı gibi girilir.',
    investigationTitle: 'İnceleme ve Kök Neden',
    notificationsTitle: 'Bildirimler',
    notificationsHint:
      'Bildirim yükümlülüğü ve süreleri bayrak devletine ve şirket SMS’ine göre değişir; sistem yalnız kararı ve kime ne zaman bildirildiğini kaydeder.',
    rail: 'İlgili bağlantılar ve belgeler',
  },
  fields: {
    initialAssessment: 'Kaptanın İlk Değerlendirmesi',
    consequences: 'Potansiyel Sonuçlar',
    existingControls: 'Mevcut Kontroller',
    likelihood: 'Olasılık',
    severity: 'Şiddet',
    workDone: 'Yapılan İşlemler',
    impedesOperation: 'Operasyonu engelliyor',
    externalAuthority: 'Dış referans (PSC / Bayrak / Klas)',
    externalReference: 'Referans No',
    closeBy: 'Kapatma süresi',
    conditions: 'Ramak kala koşulları',
    humanFactors: 'İnsan faktörleri',
    potentialHarm: 'Potansiyel zarar',
    firstResponse: 'İlk müdahale',
    pollution: 'Çevre kirliliği',
    equipmentDamage: 'Ekipman hasarı',
    injury: 'Yaralanma',
    timeline: 'Olay zaman çizelgesi',
    verificationDate: 'Doğrulama Tarihi',
    verifiedBy: 'Doğrulayan',
    vessel: 'Gemi',
    date: 'Tarih',
    time: 'Saat',
    status: 'Durum',
    priority: 'Öncelik',
    location: 'Konum',
    department: 'Departman',
    reportedBy: 'Bildiren',
    category_non_conformity: 'Uygunsuzluk Türü',
    category_deficiency: 'Kategori',
    category_near_miss: 'Kategori',
    category_accident: 'Kategori',
    source: 'Kaynak',
    subCategory: 'Alt Kategori',
    title: 'Başlık / Kısa Açıklama',
    description: 'Açıklama',
    pscAuthority: 'PSC Otoritesi',
    mou: 'MOU',
    pscPort: 'Liman',
    inspectionDate: 'Denetim Tarihi',
    inspectionNo: 'Denetim No',
    inspectorName: 'Denetçi Adı',
    reportNo: 'Rapor No',
    issuedDocument: 'Düzenlenen Belge',
    remarkReference: 'Remark Referansı',
    overallFinding: 'Genel Sonuç',
    findingNote: 'Sonuç notu',
    smsReference: 'İhlal Edilen ISM / SMS Maddesi',
    ncClass: 'Sınıf',
    rootCauseHypothesis: 'İlk kök neden tahmini',
    proposedAction: 'Önerilen düzeltici faaliyet',
    relatedAsset: 'İlgili Ekipman',
    assetNo: 'Ekipman No',
    locationDetail: 'Konum (Ayrıntılı)',
    equipmentUsable: 'Ekipman kullanılabilir mi',
    operationalImpact: 'Operasyonel etki',
    temporaryMeasure: 'Geçici tedbir / yedek ekipman',
    pmsWorkOrder: 'PMS iş emri',
    pmsDefect: 'PMS arızası',
    immediateAction: 'İlk Müdahale',
    worstOutcome: 'Olası en kötü makul sonuç',
    riskLevel: 'Risk seviyesi',
    riskMethod: 'Risk değerlendirme yöntemi',
    affected: 'Etkilenebilecek kişiler / sistemler',
    preventingFactors: 'Olayı önleyen ya da etkisini azaltan etkenler',
    injuredCount: 'Yaralı sayısı',
    emergencyResponse: 'Acil müdahale',
    injuries: 'Yaralanma / sağlık etkisi (tıbbi kayıt girilmez)',
    damage: 'Hasar',
    environmental: 'Çevresel sonuçlar',
    investigation: 'İnceleme',
    rootCause: 'Kök Neden',
    manufacturer: 'Üretici',
    model: 'Model',
    spareEquipment: 'Yedek ekipman',
    serviceNeed: 'Servis / yedek parça ihtiyacı',
    chronology: 'Kronoloji',
    similarEvents: 'Benzer geçmiş olaylar',
    shareWithFleet: 'Dersleri diğer gemilerle paylaş',
    witnesses: 'Tanık ifadeleri',
    medicalMeasures: 'Alınan tıbbi tedbirler',
    contributingFactors: 'Katkıda bulunan etkenler',
    losses: 'Kayıplar',
    lessonsLearned: 'Alınan Dersler',
    responsible: 'Sorumlu',
    targetDate: 'Hedef Tarih',
    parentReport: 'Ana olay (bağlı rapor)',
    formRecord: 'İlgili ISM form kaydı',
    notificationDecision: 'Bildirim gerekli mi',
    notificationReason: 'Gerekçe / otorite referansı',
    verificationNote: 'Etkinlik doğrulaması',
    testResult: 'Onarım / fonksiyon testi sonucu',
    fitForService: 'Hizmete uygun',
    residualRisk: 'Kalıntı risk',
  },
  hseq: {
    title: 'HSEQ Raporları',
    description:
      'Uygunsuzluk, Eksiklik, Ramak Kala ve Kaza raporları tek akışla: Bildirildi, İşlemde, Doğrulama, Kapandı.',
    newReport: 'Yeni Rapor',
    chooseType: 'Rapor türünü seçin',
    allTypes: 'Tümü',
    typeFilter: 'Rapor türü',
    typeNames: {
      non_conformity: 'Uygunsuzluk',
      deficiency: 'Eksiklik',
      near_miss: 'Ramak Kala',
      accident: 'Kaza',
    },
    stage: 'Aşama',
    allStages: 'Bütün aşamalar',
    stages: {
      reported: 'Bildirildi',
      under_action: 'İşlemde',
      verification: 'Doğrulama',
      closed: 'Kapandı',
    },
    stageNotes: {
      reported: 'Rapor gönderildi',
      under_action: 'Faaliyetler sürüyor',
      verification: 'Etkinlik kontrolü',
      closed: 'Rapor kapandı',
    },
    stepper: 'Rapor aşamaları',
    form: 'Form',
    sections: {
      details: 'Rapor Detayları',
      initial: 'İlk Değerlendirme',
      risk: 'Risk Değerlendirmesi',
      investigation: 'İnceleme / Kök Neden',
      plan: 'Düzeltici Faaliyet Planı',
      linked: 'Bağlı Kayıtlar',
      implementation: 'Uygulama',
      verification: 'Doğrulama',
      review: 'İşletme / DPA Değerlendirmesi',
      closure: 'Kapanış',
    },
    sectionHints: {
      details: 'Ne, nerede ve ne zaman oldu',
      initial: 'Kaptanın ilk değerlendirmesi ve acil tedbirler',
      risk: 'Olası sonuçlar, mevcut kontroller, olasılık ve şiddet',
      investigation: 'Olay araştırması ve kök neden',
      plan: 'Faaliyetler, sorumlular ve hedef tarihler',
      linked: 'Bu raporun arıza, iş emri ve satın alma kayıtları',
      implementation: 'Yapılan işlemler, kullanılan malzemeler ve kanıtlar',
      verification: 'Tamamlanma ve etkinlik kontrolü',
      review: 'İşletmenin değerlendirmesi ve kararı',
      closure: 'Raporun kapatılması',
    },
    readOnly: 'Salt okunur',
    typeDetails: {
      non_conformity: 'Uygunsuzluk bilgileri',
      deficiency: 'Eksiklik bilgileri',
      near_miss: 'Ramak kala bilgileri',
      accident: 'Kaza bilgileri',
    },
    meta: {
      created: 'Oluşturulma: {{at}}',
      createdBy: 'Oluşturan: {{name}}',
      type: 'Tür: {{type}}',
      category: 'Kategori: {{category}}',
      priority: 'Öncelik',
    },
    risk: {
      level: 'Risk Seviyesi',
      notRated: 'Değerlendirilmedi',
      score: 'Puan {{score}}',
      matrixHint: 'Şirketin risk matrisinde olasılık × şiddet.',
      levels: {
        very_low: 'Çok Düşük',
        low: 'Düşük',
        medium: 'Orta',
        high: 'Yüksek',
        very_high: 'Çok Yüksek',
        critical: 'Kritik',
      },
    },
    recurrence: {
      title: 'Tekrar kontrolü',
      none: 'Bu gemide benzer uygunsuzluk yok.',
      found_one: 'Bu gemide {{count}} benzer uygunsuzluk (aynı madde):',
      found_other: 'Bu gemide {{count}} benzer uygunsuzluk (aynı madde):',
    },
    implementation: {
      materials: 'Kullanılan Malzemeler',
      item: 'Parça / Kod',
      code: 'Kod',
      quantity: 'Miktar',
      unit: 'Birim',
      fromStock: 'Stoktan',
      requisition: 'Satın Alma',
      add: 'Malzeme ekle',
      remove: 'Kaldır',
      empty: 'Malzeme yazılmadı.',
      evidence: 'Kanıtlar',
      noEvidence: 'Henüz kanıt yok.',
      yes: 'Evet',
      no: 'Hayır',
    },
    verification: {
      effective: 'Düzeltici faaliyet etkin oldu mu?',
      yes: 'Evet',
      no: 'Hayır',
      date: 'Doğrulama Tarihi',
      verifiedBy: 'Doğrulayan',
      comments: 'Açıklamalar',
    },
    review: {
      empty: 'Henüz işletme değerlendirmesi yok.',
      officeNotes: 'Ofis notları',
      steps: 'Kapanış onayı',
      noSteps: 'Onay adımları rapor kapanışa gönderilince görünür.',
    },
    linked: {
      defect: 'Arıza',
      workOrder: 'İş Emri',
      requisition: 'Satın Alma Talebi',
      equipment: 'Ekipman',
      none: 'Bağlı değil',
      linked: 'Bağlı',
      cancelled: 'İptal',
      parent: 'Ana olay',
      children: 'İlgili raporlar',
      open: '{{no}} aç',
    },
    quick: {
      title: 'Hızlı İşlemler',
      createDefect: 'Arıza Oluştur',
      createWorkOrder: 'İş Emri Oluştur',
      createRequisition: 'Satın Alma Talebi Oluştur',
      smsUpdate: 'SMS Prosedürü Güncelle',
      copy: 'Raporu Kopyala',
      close: 'Raporu Kapat',
      closeBlocked: 'Kapanış için gerekenler: {{gaps}}',
      afterSubmit: 'Hızlı işlemler rapor gönderildikten sonra açılır.',
    },
    workOrder: {
      title: 'İş Emri Oluştur',
      exists: '{{no}} iş emri bu rapora zaten bağlı; ikinci iş emri açılmaz.',
      open: 'İş emrini aç',
      equipment: 'Ekipman',
      name: 'İş emri başlığı',
      target: 'Hedef tarih',
      create: 'İş emri oluştur',
      created: '{{no}} iş emri açıldı ve rapora bağlandı.',
      reused: 'Bağlı arızanın {{no}} onarım iş emri bu rapora bağlandı.',
      dateDropped:
        '{{no}} iş emri açıldı ve rapora bağlandı. Seçilen hedef tarih bu gemi sunucusunda kaydedilemedi; tarihi PMS iş emrinde düzeltin.',
      fromDefect: 'Bağlı arızanın onarım iş emri varsa yenisi açılmaz, o iş emri rapora bağlanır.',
    },
    sms: {
      title: 'SMS Prosedürü Güncelle',
      needed: 'SMS prosedürü güncellenmeli',
      procedure: 'Prosedür adı / numarası',
      proposal: 'Prosedür nasıl geliştirilebilir?',
      save: 'Kaydet',
      saved: 'Prosedür güncelleme ihtiyacı rapora yazıldı.',
      hint: 'İhtiyaç rapora yazılır ve PDF’te görünür; ofis doküman revizyonuyla takip eder.',
      recorded: 'Prosedür güncellenmeli: {{procedure}}',
    },
    copy: {
      loaded: 'Alanlar {{no}} raporundan kopyalandı. Kontrol edip yeni taslağı kaydedin.',
    },
    timeline: {
      title: 'Durum',
      created: 'Rapor oluşturuldu',
      submitted: 'Rapor gönderildi',
      reviewed: 'İşleme alındı',
      closure: 'Doğrulamaya gönderildi',
      closed: 'Kapandı',
      waiting: 'Bekliyor',
    },
    saveFirst: 'Önce raporu kaydedin.',
  },
  fieldErrors: {
    REQUIRED: 'Zorunlu',
    INVALID: 'Geçerli bir seçim değil',
    TOO_LONG: 'Çok uzun',
    INVALID_DATE: 'Geçerli bir tarih değil',
    INVALID_TIME: 'Geçerli bir tarih ve saat değil',
    INVALID_ID: 'Geçerli bir seçim değil',
  },
  review: {
    title: 'Raporu incele',
    confirm: 'İncelendi olarak işaretle',
    body: 'İlk değerlendirmenizi yazın. İncelemeden sonra düzeltici faaliyet atanabilir.',
    note: 'İnceleme notu',
  },
  actions: {
    title: 'Düzeltici Faaliyetler',
    afterSubmit: 'Faaliyetler rapor gönderilip incelendikten sonra atanır.',
    afterReview: 'Faaliyetler kaptan incelemesinden sonra atanabilir.',
    new: 'Yeni Faaliyet',
    plan: 'Faaliyet Planı',
    progress: 'İlerleme',
    closure: 'Kapanış',
    empty: 'Henüz faaliyet yok.',
    no: 'No',
    description: 'Faaliyet Açıklaması',
    responsible: 'Sorumlu',
    dueDate: 'Hedef Tarih',
    status: 'Durum',
    started: 'Başladı',
    completed: 'Tamamlandı',
    cancelled: 'İptal edildi',
    noEvidence: 'Henüz kanıt yok: tamamlamadan önce fotoğraf ya da belge ekleyin.',
    start: 'Başlat',
    addEvidence: 'Kanıt ekle',
    complete: 'Tamamla',
    cancel: 'Faaliyeti iptal et',
    cancelTitle: 'Faaliyeti iptal et',
    cancelBody: 'Faaliyet gerekçenizle geçmişte kalır.',
    editTitle: 'Faaliyeti düzenle',
    newTitle: 'Yeni faaliyet',
    save: 'Kaydet',
    assign: 'Ata',
    kind: 'Faaliyet türü',
    person: 'Atanan kişi',
    finding: 'PSC remark’ı / bulgu',
    workOrder: 'PMS iş emri',
    formRecord: 'ISM form kaydı',
    completeTitle: '#{{no}} faaliyetini tamamla',
    evidenceCount_one: '{{count}} kanıt dosyası ekli.',
    evidenceCount_other: '{{count}} kanıt dosyası ekli.',
    evidenceNeeded:
      'Faaliyet kanıtsız tamamlanamaz: önce fotoğraf ya da belge ekleyin ya da raporlanmış PMS iş emrini veya gönderilmiş ISM form kaydını bağlayın.',
    completionNote: 'Ne yapıldı',
  },
  findings: {
    title: 'PSC Remark Ayrıntıları',
    subtitle: 'Remark Kalemleri',
    add: 'Remark Ekle',
    empty: 'Henüz remark yok.',
    afterSave: 'Önce raporu kaydedin, sonra remark’ları ekleyin.',
    no: 'No',
    issue: 'Konu / Remark',
    category: 'Kategori',
    regulation: 'Mevzuat Ref.',
    dueDate: 'Hedef Tarih',
    status: 'Durum',
    action: 'İşlem',
    edit: 'Düzenle',
    view: 'Görüntüle',
    editTitle: 'Remark’ı düzenle',
    newTitle: 'Remark ekle',
    save: 'Kaydet',
    code: 'PSC kodu',
    codeHint: 'PSC raporunda yazıldığı gibi',
    actionTaken: 'Alınan önlem kodu',
  },
  closure: {
    title: 'Kapanış',
    verificationTitle: 'Doğrulama',
    checklist: 'Kapanış koşulları',
    checks: CHECKS_TR,
    gaps: CHECKS_TR,
    steps: 'Onay adımları',
    office: 'ofis',
    decisions: { approved: 'Onaylandı', returned: 'İade edildi' },
    waiting: 'bekliyor',
    closedBanner: 'Kapandı. Kayıt kilitli; gerekçeyle yeniden açılabilir.',
    reopenedBanner:
      'Yeniden açıldı: doğrulama başarısız oldu ya da sorun tekrarladı. Yeni faaliyet ekleyin; önceki kayıt saklanır.',
    submit: 'Kapanışa gönder',
    submitTitle: 'Kapanışa gönder',
    submitBody:
      'Kapanış incelenirken rapor, faaliyetleri ve kanıtları kilitlenir. Raporu açan kişi tek başına kapatamaz.',
    saveFirst: 'Kapanışa göndermeden önce değişikliklerinizi kaydedin.',
    approve: 'Kapanışı onayla',
    approveTitle: 'Kapanışı onayla',
    approveBody: 'Faaliyetlerin etkili olduğunu ve raporun kapanabileceğini teyit edin.',
    return: 'İade et',
    returnTitle: 'İade et (doğrulama başarısız)',
    returnBody: 'Rapor yeni faaliyet için yeniden açılır. Gerekçe zorunludur.',
    reopen: 'Yeniden aç',
    reopenTitle: 'Raporu yeniden aç',
    reopenBody: 'Kapanmış kayıt silinmez; gerekçe ve yeni faaliyetler geçmişte kalır.',
  },
  notes: {
    add: 'Ofis notu ekle',
    title: 'Ofis notu',
    send: 'Gemiye gönder',
    kind: 'Not türü',
    body: 'Not',
  },
  requests: {
    title: 'Gemiden rapor iste',
    send: 'Talebi gönder',
    body: 'Raporlar gemide yazılır. Gemi raporu talebinizden açar (ör. iç denetim bulgusu).',
    type: 'Rapor türü',
  },
  files: {
    title: 'Fotoğraflar / Belgeler',
    drop: 'Fotoğraf ya da belge ekleyin',
    afterSave: 'Dosya eklemek için önce raporu kaydedin.',
    formats: 'Desteklenen biçimler: JPG, PNG, PDF (en çok 25 MB)',
    choose: 'Dosya seç',
    empty: 'Dosya yok.',
    uploaded: 'Dosya eklendi.',
    voided: 'İptal edildi',
    states: { uploading: 'Gemiden yükleniyor', on_vessel: 'Dosya gemide', rejected: 'Reddedildi' },
    reportFiles: 'Raporun dosyaları',
    actionFiles: 'Faaliyetin kanıtı',
  },
  links: {
    title: 'İlgili Bağlantılar',
    empty: 'Bağlantılar rapor kaydedilince görünür.',
    parent: 'Ana olay',
    children_one: 'Bağlı kayıtlar ({{count}})',
    children_other: 'Bağlı kayıtlar ({{count}})',
    workOrder: 'PMS iş emri',
    defect: 'PMS arızası',
    formRecord: 'ISM form kaydı',
    officeRequest: 'Ofis talebi',
    history: 'Geçmiş',
  },
  comments: {
    title: 'Yorumlar / Takip',
    afterSubmit: 'Yorumlar rapor kaydedilince açılır.',
    empty: 'Henüz yorum yok.',
    ship: 'Gemi yorumu',
    add: 'Yorum ekle',
    send: 'Yorumu gönder',
  },
  history: {
    title: 'Geçmiş / Denetim İzi',
    empty: 'Henüz geçmiş yok.',
    events: {
      created: 'Rapor açıldı',
      submitted: 'Rapor gönderildi',
      reviewed: 'İncelendi',
      action_assigned: 'Faaliyet atandı',
      action_in_progress: 'Faaliyet başladı',
      action_completed: 'Faaliyet tamamlandı',
      action_cancelled: 'Faaliyet iptal edildi',
      finding_added: 'PSC remark’ı eklendi',
      closure_submitted: 'Kapanışa gönderildi',
      closure_approved: 'Kapanış onaylandı',
      closure_returned: 'Kapanış iade edildi',
      reopened: 'Yeniden açıldı',
      comment: 'Yorum',
      office_comment: 'Ofis yorumu',
      office_action_request: 'Ofis ek faaliyet istedi',
      office_reopen_request: 'Ofis yeniden açmayı istedi',
    },
  },
  related: {
    title: 'İlgili Eksiklikler',
    subtitle: 'Aynı olaydan açılan kayıtlar bu rapora bağlanır.',
    empty: 'Bağlı kayıt yok.',
    newDeficiency: 'Yeni Eksiklik',
  },
  notifications: {
    recipient: 'Bildirilen taraf',
    name: 'Ad',
    notifiedOn: 'Gönderildi',
    dueDate: 'Son gün',
    method: 'Yol',
    reference: 'Referans',
    remove: 'Kaldır',
    add: 'Bildirim ekle',
    empty: 'Kayıtlı bildirim yok.',
  },
  dialog: {
    close: 'Kapat',
    cancel: 'Vazgeç',
    reason: 'Gerekçe',
    password: 'Parola',
    passwordHint: 'Şirket her onayda parola istiyor.',
  },
  errors: {
    GENERIC: 'İşlem tamamlanamadı ({{code}}).',
    // Kaptana ait alan (ADR 0127)
    MASTER_ONLY:
      'Kaptanın ilk değerlendirmesini yalnız kaptan yazar (ISM Kod 5). Kaptan ataması yokken Ayarlar’da seçilen vekil kaptan adına yazabilir.',
    NETWORK: 'Sunucuya bağlantı yok.',
    UNAUTHENTICATED: 'Oturumunuz sona erdi. Lütfen yeniden giriş yapın.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Rapor bulunamadı.',
    VALIDATION_FAILED: 'Bazı girişler geçerli değil.',
    ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yenileyip yeniden deneyin.',
    INVALID_HSE_REPORT: 'Lütfen zorunlu alanları tamamlayın.',
    INVALID_HSE_ACTION: 'Faaliyet eksik.',
    INVALID_HSE_FINDING: 'Remark eksik.',
    INVALID_HSE_COMMENT: 'Yorum boş ya da çok uzun.',
    INVALID_HSE_NOTE: 'Not eksik.',
    HSE_DRAFT_OWNER: 'Taslağı yalnız açan kişi değiştirebilir.',
    HSE_REPORT_SUBMITTED: 'Rapor zaten gönderildi.',
    HSE_NOT_SUBMITTED: 'Rapor henüz gönderilmedi.',
    HSE_NOT_REVIEWED: 'Faaliyetler kaptan incelemesinden sonra atanabilir.',
    HSE_ALREADY_REVIEWED: 'Rapor zaten incelendi.',
    HSE_PENDING_CLOSURE: 'Kapanış onay bekliyor; rapor kilitli.',
    HSE_CLOSED: 'Rapor kapandı ve kilitli.',
    HSE_NOT_CLOSABLE: 'Rapor henüz kapanışa gönderilemez:',
    HSE_ACTION_FINISHED: 'Faaliyet tamamlandı ya da iptal edildi; değiştirilemez.',
    HSE_FINDING_TYPE: 'Remark yalnız uygunsuzluğa eklenir.',
    HSE_FINDING_IN_USE: 'Remark’ın faaliyeti var; kaldırılamaz.',
    HSE_FINDING_MISMATCH: 'Remark başka bir rapora ait.',
    HSE_PARENT_INVALID: 'Ana olay bu rapor ya da bağlı bir kayıt olamaz.',
    HSE_REQUEST_USED: 'Bu talepten zaten rapor açıldı.',
    HSE_NUMBER_FIXED: 'Rapor türü ve numarası değiştirilemez.',
    EVIDENCE_REQUIRED:
      'Faaliyet kanıtsız tamamlanamaz. Fotoğraf ya da belge ekleyin ya da raporlanmış PMS iş emrini veya gönderilmiş form kaydını bağlayın.',
    COMPLETION_NOTE_REQUIRED: 'Ne yapıldığını yazın.',
    INVALID_ACTION_TRANSITION: 'Faaliyet bu duruma geçemez.',
    CANCEL_REASON_REQUIRED: 'Gerekçe zorunludur.',
    DISCARD_REASON_REQUIRED: 'Gerekçe zorunludur.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    VESSEL_MISMATCH: 'Bağlı kayıt başka bir gemiye ait.',
    RISK_ASSESSMENT_NOT_AVAILABLE: 'Risk değerlendirmesi henüz yok.',
    REAUTHENTICATION_REQUIRED: 'Parolanız gerekir.',
    INVALID_CREDENTIALS: 'Parola doğru değil.',
    TOO_MANY_ATTEMPTS: 'Çok fazla deneme. Biraz bekleyip yeniden deneyin.',
    NO_ELIGIBLE_APPROVER:
      'Gemide bu kapanışı onaylayabilecek kimse yok (raporu açan kişi onaylayamaz). Ofisten onay matrisini kontrol etmesini isteyin.',
    SELF_APPROVAL_FORBIDDEN: 'Raporu açan ya da gönderen kişi onaylayamaz.',
    SAME_APPROVER_FORBIDDEN: 'Bu kapanışın başka bir adımını zaten onayladınız.',
    NOT_APPROVER: 'Rolünüz bu adımı onaylamaz.',
    OFFICE_STEP: 'Bu adımın kararı ofiste verilir.',
    NOT_OFFICE_STEP: 'Bu adımın kararı gemide verilir.',
    NOT_PENDING: 'Karar bekleyen bir şey yok.',
    STALE_DECISION: 'Siz bakarken rapor değişti. Yenileyip yeniden deneyin.',
    CONTENT_CHANGED: 'Rapor gönderildikten sonra değişti. Yenileyip yeniden deneyin.',
    RETURN_REASON_REQUIRED: 'İade için gerekçe zorunludur.',
    REOPEN_REASON_REQUIRED: 'Yeniden açma için gerekçe zorunludur.',
    NOT_FINAL: 'Yalnız kapanmış rapor yeniden açılır.',
    ALREADY_SUBMITTED: 'Kapanış zaten gönderildi.',
    FILE_NOT_UPLOADED:
      'Kanıt dosyaları henüz ofise ulaşmadı. Karar dosyalar yüklenip doğrulanınca verilebilir.',
    FILE_TOO_LARGE: 'Dosya 25 MB’tan büyük.',
    UNSUPPORTED_FILE_TYPE: 'Yalnız JPG, PNG ve PDF dosyaları eklenebilir.',
    HSE_WORK_ORDER_EXISTS: 'Bu rapora zaten bir iş emri bağlı.',
    EQUIPMENT_REQUIRED: 'İş emrinin ekipmanını seçin.',
  },
};
